import GreenBg from "../../assets/images/comman/green-bg.png";
import WhiteBg from "../../assets/images/comman/white-bg.png";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateTeamPointsAPI } from "../../api/game";
import { useParams } from "react-router-dom";
import useToast from "../../hooks/useToast";
interface Lifeline {
  lifeline_id: {
    name: { en: string; ar: string };
    description: { en: string; ar: string };
    image: string;
    _id: string;
    slug?: string;
  };
  is_used: boolean;
  _id: string;
}

interface GameLifelineProps {
  team?: any;
  score: number | string;
  lifelines: Lifeline[];
  onLifelineClick?: (lifeline: Lifeline, teamId: string, teamName: string) => void;
  isCurrentTurn?: boolean;
  scoreEditable?: boolean;
  /** When true, no lifelines can be used (e.g. one lifeline already used this round) */
  disableAllLifelines?: boolean;
  disabledLifelineSlugs?: string[];
}

const GameLifeline = ({
  team,
  score,
  lifelines,
  onLifelineClick,
  isCurrentTurn = true,
  scoreEditable = false,
  disableAllLifelines = false,
  disabledLifelineSlugs = [],
}: GameLifelineProps) => {
  const baseUrl = import.meta.env.VITE_BASE_CDN_URL;
  const displayTeamName = team?.team_name || "";
  const teamId = team?._id || "";
  const { gameId } = useParams();
  const queryClient = useQueryClient();
  const [localScore, setLocalScore] = useState(String(score ?? ""));
  const [isUpdating, setIsUpdating] = useState(false);

  // Keep input score in sync when server score changes (e.g. after lifeline effects).
  useEffect(() => {
    setLocalScore(String(score ?? ""));
  }, [score]);
  const handleLifelineClick = (lifeline: Lifeline) => {
    // Don't allow clicking if lifeline is already used, it's not the team's turn, or another lifeline was used this round
    const isSlugDisabled = disabledLifelineSlugs?.includes(lifeline.lifeline_id?.slug || "");
    if (lifeline.is_used || !isCurrentTurn || disableAllLifelines || isSlugDisabled) return;

    // Call the onClick handler if provided
    if (onLifelineClick) {
      onLifelineClick(lifeline, teamId, displayTeamName);
    }
  };

  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as "en" | "ar";

  const { mutate: updateTeamPoints } = useMutation({
    mutationFn: updateTeamPointsAPI,
    onSuccess: (data) => {
      useToast(data?.message || "Score updated successfully", "success");
      // Refresh game details so board reflects latest scores
      queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
    },
    onError: (error: any) => {
      useToast(error.message || "Failed to update score", "error");
      // Revert to original score on error
      setLocalScore(String(score ?? ""));
    },
    onSettled: () => {
      setIsUpdating(false);
    },
  });

  const handleScoreCommit = () => {
    if (!gameId || !teamId) return;
    const trimmed = localScore.trim();
    if (trimmed === "") {
      // Empty is not allowed, revert
      setLocalScore(String(score ?? ""));
      return;
    }
    // Only fire API if value actually changed
    if (trimmed === String(score ?? "")) return;

    setIsUpdating(true);
    updateTeamPoints({
      game_id: gameId as string,
      team_id: teamId,
      points: trimmed,
    });
  };

  const handleScoreKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
      // handleScoreCommit();
    } else if (e.key === "Escape") {
      // Revert on escape
      setLocalScore(String(score ?? ""));
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only digits (and empty string)
    if (/^\d*$/.test(value)) {
      setLocalScore(value);
    }
  };

  return (
    // 2xl:pt-[0.57vw] lg:pt-2.5 pt-1
    <div className={` 2xl:rounded-[0.83vw] lg:rounded-2xl  rounded-xl text-center overflow-hidden`}>
      {/* "bg-light-periwinkle-blue" */}
      <div className="2xl:max-w-full lg:max-w-full max-w-full 2xl:text-[1.2375vw]/none xl:text-lg/none lg:text-base/none text-sm/none font-garamond font-bold landscape:max-lg:text-xs/none capitalize">
        {displayTeamName}
      </div>

      <div className={`text-center font-garamond font-bold text-primary-rose 2xl:text-[1.875vw]/tight xl:text-xl/none text-base/none
       p-1 2xl:p-[0.208vw] flex justify-center items-center md:bg-size-[91%_90%] w-full mx-auto bg-center bg-no-repeat 2xl:my-[0.416vw] xl:my-1.5 md:my-1 my-0 landscape:max-lg:bg-size-[70%_70%] bg-size-[80%_80%] landscape:max-lg:my-0 bg-brown-primary 2xl:rounded-[0.417vw] rounded-lg
       `} >
        {/* ${isCurrentTurn ? "bg-[#C9D863]" : "bg-white"} */}
        {/* style={{ backgroundImage: `url(${isCurrentTurn ? GreenBg : WhiteBg})` }} */}
        <input
          type="text"
          value={localScore}
          onChange={scoreEditable ? handleScoreChange : undefined}
          onBlur={scoreEditable ? handleScoreCommit : undefined}
          onKeyDown={scoreEditable ? handleScoreKeyDown : undefined}
          disabled={isUpdating || !scoreEditable}
          readOnly={!scoreEditable}
          className="landscape:max-lg:pt-0 md:pt-1 pt-0.5  outline-none text-center w-full
          landscape:max-lg:text-[2vw] 2xl:text-[1.875vw]/tight xl:text-xl/none text-base/none font-garamond font-bold text-primary-rose [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-brown-primary 2xl:rounded-[0.417vw] rounded-lg 2xl:border-[0.15vw] border-2 border-primary-rose disabled:opacity-100 disabled:cursor-default"
        />
      </div>
      {
        lifelines?.length > 0 && (
          <div className="w-fit max-w-full mx-auto relative p-0.5 pb-0">
            <div
              className={`flex gap-1 justify-between 2xl:rounded-t-[0.78125vw] rounded-t-2xl rounded-b-0 rounded-b-0 items-center mx-auto landscape:max-lg:justify-evenly`}
            >
              <div className="2xl:gap-[0.8vw] md:gap-4 gap-2 justify-between w-full  max-w-full transition-all p-0 bg-transparent border-none cursor-pointer flex items-center relative">
                {/* Regular lifelines */}
                {lifelines?.map((lifeline, index) => {
                  const isUsed = lifeline.is_used;
                  const isSlugDisabled = disabledLifelineSlugs?.includes(lifeline.lifeline_id?.slug || "");
                  const isClickable = !isUsed && isCurrentTurn && !disableAllLifelines && !isSlugDisabled && !!onLifelineClick;
                  const isDisabled = isUsed || !isCurrentTurn || disableAllLifelines || isSlugDisabled;

                  return (
                    <button
                      key={lifeline._id || index}
                      onClick={() => handleLifelineClick(lifeline)}
                      disabled={isDisabled}
                      className={`2xl:w-[3vw] xl:w-12 w-10 2xl:h-[3vw] xl:h-12 md:h-10 h-8 transition-all p-0.5 bg-transparent border-none ${isDisabled
                        ? "grayscale opacity-50 cursor-not-allowed"
                        : isClickable
                          ? "cursor-pointer hover:scale-110 hover:drop-shadow-lg active:scale-95"
                          : ""
                        }`}
                      title={lifeline.lifeline_id?.description?.[currentLanguage] || lifeline.lifeline_id?.name?.en || ""}
                    >
                      <img
                        src={`${baseUrl}/${lifeline.lifeline_id?.image}`}
                        alt={lifeline.lifeline_id?.name?.en || "Lifeline"}
                        className="h-full w-full object-contain"
                      // className="2xl:w-[2.5vw] xl:w-10 w-8 2xl:h-[2.5vw] xl:h-10 h-8 object-contain"
                      />
                    </button>
                  );
                })}

              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default GameLifeline;
